import React from 'react';
import { Loader2, Check, ArrowLeft } from 'lucide-react';
import AppLogo from '../components/AppLogo';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { sendCode, submitCode, isMockAuth, DEMO_CODE } from '../lib/otp';
import { recordSignupConsent } from '../lib/consent';

/**
 * FrontDoor — the login-first front door.
 *
 * Passwordless by design: GoTrue emails a link and a 6-digit code, and either
 * signs the member in. No password field, no social login, and the Log In tab
 * never reveals whether an address has an account.
 *
 * Two fields only. Name is collected later, at the first commitment that
 * actually needs it, rather than as a toll on the way in.
 */

const PRIVACY_VERSION = process.env.REACT_APP_PRIVACY_VERSION || '2026-09';
const RESEND_SECONDS = 60;

const DIAL_CODES = [
  { code: '+65', label: 'SG +65' },
  { code: '+60', label: 'MY +60' },
  { code: '+62', label: 'ID +62' },
  { code: '+673', label: 'BN +673' },
  { code: '+91', label: 'IN +91' },
  { code: '+880', label: 'BD +880' },
  { code: '+63', label: 'PH +63' },
  { code: '+44', label: 'UK +44' },
  { code: '+61', label: 'AU +61' },
  { code: '+1', label: 'US +1' },
];

const emailLooksValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

function mobileError(dial, digits) {
  if (!digits) return 'Please enter your mobile number.';
  if (dial === '+65') {
    if (!/^[689]\d{7}$/.test(digits)) return 'A Singapore number is 8 digits starting 6, 8 or 9.';
    return null;
  }
  if (digits.length < 5 || digits.length > 14) return 'That does not look like a mobile number.';
  return null;
}

function maskEmail(email) {
  const [name, domain] = String(email).split('@');
  if (!domain) return email;
  const head = name.slice(0, 1);
  return `${head}${'•'.repeat(Math.max(2, Math.min(name.length - 1, 3)))}@${domain}`;
}

/**
 * ConsentRow — deliberately ONE component used at ONE size for both consents.
 *
 * The PDPA row is required and the marketing row is optional, but they look
 * identical and the marketing box always starts unticked. Consent collected by
 * a design meant to be ticked by accident is not consent, and it would inflate
 * the exact number we report while producing contacts we cannot lawfully message.
 */
function ConsentRow({ id, checked, onChange, title, body, requirement, children }) {
  return (
    <label
      htmlFor={id}
      data-testid={id}
      className="flex items-start gap-3 w-full rounded-2xl border border-border bg-white p-3 cursor-pointer transition-colors hover:bg-muted/40"
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-7 w-7 flex-shrink-0 rounded-md border-2 border-border accent-[#0F4C4A] cursor-pointer"
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-1.5">
          <span className="text-sm font-bold text-foreground leading-snug">{title}</span>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {requirement}
          </span>
        </span>
        <span className="block text-xs text-muted-foreground leading-snug mt-0.5">
          {body}
          {children}
        </span>
      </span>
    </label>
  );
}

export default function FrontDoor() {
  const [tab, setTab] = React.useState('signup');
  const [stage, setStage] = React.useState('form'); // form | sent

  const [email, setEmail] = React.useState('');
  const [dial, setDial] = React.useState('+65');
  const [mobile, setMobile] = React.useState('');
  const [pdpa, setPdpa] = React.useState(false);
  const [marketing, setMarketing] = React.useState(false);

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [code, setCode] = React.useState('');
  const [codeError, setCodeError] = React.useState(null);
  const [secondsLeft, setSecondsLeft] = React.useState(0);

  const isSignup = tab === 'signup';
  const mobileDigits = mobile.replace(/\D/g, '');

  React.useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const switchTab = (next) => {
    setTab(next);
    setError(null);
  };

  const consentPayload = () => ({
    mobile: `${dial}${mobileDigits}`,
    consent: {
      pdpa: true,
      marketing,
      policy_version: PRIVACY_VERSION,
      captured_at: new Date().toISOString(),
      source: 'front-door',
    },
  });

  const send = async ({ resend = false } = {}) => {
    setError(null);
    if (!emailLooksValid(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (isSignup && !resend) {
      const problem = mobileError(dial, mobileDigits);
      if (problem) {
        setError(problem);
        return;
      }
      if (!pdpa) {
        setError('Please agree to the privacy notice to create your account.');
        return;
      }
    }

    setBusy(true);
    const result = await sendCode({
      email: email.trim(),
      createUser: isSignup,
      data: isSignup ? consentPayload() : undefined,
    });
    setBusy(false);

    if (!result.ok) {
      setError(result.error || 'Could not send the email. Please try again.');
      return;
    }
    if (isSignup) recordSignupConsent(consentPayload());
    setCode('');
    setCodeError(null);
    setSecondsLeft(RESEND_SECONDS);
    setStage('sent');
  };

  const verify = async (value) => {
    setCodeError(null);
    setBusy(true);
    const ok = await submitCode(email.trim(), value);
    setBusy(false);
    if (!ok) {
      setCodeError('That code did not work. Check it, or send a new one.');
      setCode('');
    }
    // On success the session lands in storage and App re-renders the app; this
    // component simply unmounts.
  };

  const onCodeChange = (value) => {
    setCode(value);
    if (value.length === 6) verify(value);
  };

  const canSubmit = isSignup
    ? emailLooksValid(email) && Boolean(mobileDigits) && pdpa
    : emailLooksValid(email);

  return (
    <div
      className="min-h-full w-full bg-background overflow-y-auto"
      data-testid="front-door"
    >
      <div className="mx-auto max-w-[480px] px-5 pt-5 pb-6 flex flex-col">
        <div className="flex flex-col items-center">
          <AppLogo size="md" />
          <h1 className="mt-2 text-xl font-bold tracking-tight text-foreground">IhsanOne</h1>
        </div>

        {isMockAuth() && (
          <p
            data-testid="fd-demo-notice"
            className="mt-4 rounded-xl border border-[#D4A843]/50 bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-900 text-center leading-relaxed"
          >
            Demo mode — no email is sent and no account is created.
            Use the code <span className="font-mono tracking-wider">{DEMO_CODE}</span>.
          </p>
        )}

        {stage === 'form' ? (
          <>
            <div className="mt-4 grid grid-cols-2 rounded-2xl bg-muted p-1" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={isSignup}
                data-testid="fd-tab-signup"
                onClick={() => switchTab('signup')}
                className={`h-12 rounded-xl text-sm font-bold transition-colors ${
                  isSignup ? 'bg-white text-[#0F4C4A] shadow-sm' : 'text-muted-foreground'
                }`}
              >
                Sign up
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={!isSignup}
                data-testid="fd-tab-login"
                onClick={() => switchTab('login')}
                className={`h-12 rounded-xl text-sm font-bold transition-colors ${
                  !isSignup ? 'bg-white text-[#0F4C4A] shadow-sm' : 'text-muted-foreground'
                }`}
              >
                Log in
              </button>
            </div>

            <form
              className="mt-4 space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                send();
              }}
            >
              <div>
                <label htmlFor="fd-email" className="block text-xs font-semibold text-foreground mb-1.5">
                  Email address
                </label>
                <input
                  id="fd-email"
                  data-testid="fd-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  inputMode="email"
                  enterKeyHint={isSignup ? 'next' : 'go'}
                  className="w-full h-12 rounded-xl border border-border bg-white px-3.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#14B8B8]"
                />
              </div>

              {isSignup && (
                <div>
                  <label htmlFor="fd-mobile" className="block text-xs font-semibold text-foreground mb-1.5">
                    Mobile number
                  </label>
                  <div className="flex gap-2">
                    <select
                      aria-label="Country code"
                      value={dial}
                      onChange={(event) => setDial(event.target.value)}
                      className="h-12 w-[104px] flex-shrink-0 rounded-xl border border-border bg-white px-2 text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-[#14B8B8]"
                    >
                      {DIAL_CODES.map((entry) => (
                        <option key={entry.code} value={entry.code}>{entry.label}</option>
                      ))}
                    </select>
                    <input
                      id="fd-mobile"
                      data-testid="fd-mobile"
                      type="tel"
                      value={mobile}
                      onChange={(event) => setMobile(event.target.value)}
                      placeholder="8123 4567"
                      autoComplete="tel-national"
                      inputMode="numeric"
                      enterKeyHint="go"
                      className="flex-1 min-w-0 h-12 rounded-xl border border-border bg-white px-3.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#14B8B8]"
                    />
                  </div>
                </div>
              )}

              {isSignup && (
                <div className="space-y-2">
                  <ConsentRow
                    id="fd-consent-pdpa"
                    checked={pdpa}
                    onChange={setPdpa}
                    title="MTFA's privacy notice"
                    body="How we use and protect your data. "
                    requirement="Required"
                  >
                    <a
                      href="/privacy.html"
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      className="underline font-semibold text-[#0F4C4A]"
                    >
                      Read it
                    </a>
                  </ConsentRow>

                  <ConsentRow
                    id="fd-consent-marketing"
                    checked={marketing}
                    onChange={setMarketing}
                    title="Be first to know"
                    body="Bursaries, events and volunteer calls — by WhatsApp or email. Stop any time."
                    requirement="Optional"
                  />
                </div>
              )}

              {!isSignup && (
                <p className="text-xs text-muted-foreground leading-relaxed">
                  MTFA staff: use your staff email address.
                </p>
              )}

              {error && (
                <p role="alert" data-testid="fd-error" className="text-xs font-semibold text-rose-600 leading-relaxed">
                  {error}
                </p>
              )}

              <button
                type="submit"
                data-testid="fd-submit"
                disabled={busy}
                aria-disabled={!canSubmit}
                className={`w-full h-12 rounded-full text-sm font-bold flex items-center justify-center gap-2 transition-colors ${
                  canSubmit ? 'bg-[#0F4C4A] text-white' : 'bg-[#0F4C4A]/40 text-white'
                }`}
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSignup ? 'Sign up' : 'Email me a sign-in link'}
              </button>
            </form>
          </>
        ) : (
          <div className="mt-8">
            <button
              type="button"
              data-testid="fd-wrong-email"
              onClick={() => { setStage('form'); setError(null); }}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Wrong email?
            </button>

            <div className="mt-4 flex flex-col items-center text-center">
              <span className="h-12 w-12 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                <Check className="h-6 w-6 text-[#0F4C4A]" />
              </span>
              <h2 className="mt-3 text-lg font-bold text-foreground">Check your email</h2>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                We sent a link and a 6-digit code to{' '}
                <span className="font-semibold text-foreground">{maskEmail(email)}</span>.
              </p>
            </div>

            <div className="mt-6 flex flex-col items-center">
              <label htmlFor="fd-code" className="block text-xs font-semibold text-foreground mb-2">
                Enter the 6-digit code
              </label>
              <InputOTP
                id="fd-code"
                data-testid="fd-code"
                maxLength={6}
                value={code}
                onChange={onCodeChange}
                disabled={busy}
                containerClassName="gap-2"
              >
                <InputOTPGroup className="gap-2">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <InputOTPSlot
                      key={index}
                      index={index}
                      className="h-12 w-11 rounded-xl border text-base font-bold"
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>

              {busy && (
                <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Signing you in…
                </p>
              )}

              {codeError && (
                <p role="alert" data-testid="fd-code-error" className="mt-3 text-xs font-semibold text-rose-600 text-center">
                  {codeError}
                </p>
              )}

              <button
                type="button"
                data-testid="fd-resend"
                disabled={secondsLeft > 0 || busy}
                onClick={() => send({ resend: true })}
                className="mt-5 text-xs font-semibold text-[#0F4C4A] disabled:text-muted-foreground"
              >
                {secondsLeft > 0
                  ? `Resend in 0:${String(secondsLeft).padStart(2, '0')}`
                  : 'Send a new code'}
              </button>

              {/* Never says whether the address has an account — it only hints. */}
              <p className="mt-5 text-[11px] text-muted-foreground text-center leading-relaxed max-w-[260px]">
                Nothing after two minutes? You may not have an account yet —{' '}
                <button
                  type="button"
                  onClick={() => { setStage('form'); switchTab('signup'); }}
                  className="underline font-semibold"
                >
                  sign up
                </button>
                .
              </p>
            </div>
          </div>
        )}

        <p className="mt-5 text-center text-[11px] text-muted-foreground">
          <a href="/privacy.html" target="_blank" rel="noreferrer" className="underline">Privacy notice</a>
          {' · '}
          <a href="/terms.html" target="_blank" rel="noreferrer" className="underline">Terms</a>
        </p>
      </div>
    </div>
  );
}
